import { AnalyticsEventName } from '@dupe/common/constants'
import { todayStamp } from './dailyConversionId'
import type { DupeSearchType } from './googleAdsConversions'

/**
 * Meta (Facebook) pixel copy of `Performed Dupe` (DUP-2091) — the Meta twin
 * of `googleAdsConversions.ts`, fired from the same call sites so both
 * platforms see the same definition of "a dupe performed".
 *
 * Same EVENT NAME as ever, new firing rule. Meta has real ad-spend history on
 * `Performed Dupe` this year, so unlike Google (which had none and got the
 * fresh `dupe_performed_manual2`), Meta keeps its event — marketing asked for
 * the existing event fixed, not reinvented. What changes is only WHEN it
 * fires: the pixel copy used to ride `useAnalytics.track({sendToFb})` on the
 * internal `Performed Dupe` emitters, inheriting chat's once-per-THREAD gate —
 * which is exactly why image searches later in a conversation, second
 * searches, and every Dupe Max turn reported nothing (the DUP-2091 QA
 * findings). Now it fires here, once per SEARCH. The internal analytics event
 * and its gate are untouched (widening them would move ~71% of the event's
 * volume and break every rate built on it); the emitters this module covers
 * simply stopped passing `sendToFb` so Meta can't be told twice.
 *
 * ⚠️ This module's only transport is the Meta browser pixel loaded in
 * `apps/web/src/components/providers/AppProviders.tsx` (`fb-pixel-base` /
 * `fb-pixel-loader`). Removing those scripts kills this conversion with no
 * error and no test failure — every call here just finds `window.fbq` absent
 * and no-ops. See docs/features/active/zaraz-migration/TODO.md before
 * touching them.
 *
 * ⚠️ Do not delete this module because the CAPI event exists. The two are
 * PEERS, not a migration: `metaAdsConversion.ts` fires from the same call
 * sites with the same transaction id, and Meta collapses the pair on
 * `event_id` (see the `fireOnce` doc below). The pixel is the half that
 * carries `fbp`/`fbc` and the real client IP and user agent — the CAPI
 * destination sends NO IP or `fbp` at all today, so losing this path costs
 * match quality on every search, not just redundancy. An intermediate cut of
 * DUP-2091's CAPI work deleted this file and had to restore it.
 */

const firedConversions = new Set<string>()
const STORAGE_PREFIX = 'meta-conversion:'

/**
 * The conversion is capped per DAY (see `dailyConversionId.ts`), so the mark
 * has to outlive a tab — `sessionStorage` is per-tab, which would let a second
 * tab re-fire the same day's conversion. `localStorage` is what makes the cap
 * hold locally rather than leaning entirely on Meta's own `eventID` dedup.
 *
 * Keys are self-expiring by construction: they carry the date, so yesterday's
 * key is simply never read again. `pruneStaleMarks` sweeps them, and the
 * undated fallback keys, so the store doesn't grow without bound.
 */
const hasFired = (key: string): boolean => {
  if (firedConversions.has(key)) return true

  try {
    return window.localStorage.getItem(`${STORAGE_PREFIX}${key}`) !== null
  } catch {
    // localStorage can be unavailable in privacy-restricted browsers. The
    // module-level set still prevents duplicate React effects in that case.
    return false
  }
}

/**
 * Every prefix this sweep is responsible for. `metaAdsConversion.ts` moved to
 * `localStorage` for the same reason this module did, so its keys need the
 * same sweep — they are listed HERE rather than pruned there so there is one
 * place that knows what a Meta conversion mark looks like. Adding a third Meta
 * transport means adding its prefix to this array, nothing else.
 */
export const META_CONVERSION_STORAGE_PREFIXES = [
  'meta-conversion:',
  'meta-ads-conversion:',
]

const DATED_KEY = /:\d{4}-\d{2}-\d{2}$/
const UNDATED_MARK_TTL_MS = 24 * 60 * 60 * 1000

/**
 * Drop marks that can no longer be read. Without this `localStorage` gains
 * keys forever — the cost of the durability that makes the daily cap work.
 * Best-effort and silent: a storage failure here must never block a
 * conversion.
 *
 * Three classes, deliberately handled differently:
 *
 *   registration — retained until browser storage is cleared: an account's
 *   conversion identity does not expire with a search's daily cap.
 *
 *   dated (`…:<user>:<YYYY-MM-DD>`) — anything not from today is dead, since
 *   the id it would match can never be generated again.
 *
 *   undated (the no-`tempUserId` fallback's per-search key) — NOT safe to drop
 *   on sight: removing one mid-session lets that same search re-fire, which is
 *   the double-count this whole change exists to remove. They are aged out on
 *   their stored timestamp instead, so the guarantee holds for the session
 *   that needs it without the key living forever. A legacy `'1'` value parses
 *   to 1 and is therefore treated as ancient, which is correct — those predate
 *   the cap.
 */
const pruneStaleMarks = (): void => {
  try {
    const today = todayStamp()
    const cutoff = Date.now() - UNDATED_MARK_TTL_MS
    const stale: string[] = []

    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i)
      if (!k || !META_CONVERSION_STORAGE_PREFIXES.some((p) => k.startsWith(p)))
        continue

      if (k.startsWith(`${STORAGE_PREFIX}CompleteRegistration:`)) continue

      if (DATED_KEY.test(k)) {
        if (!k.endsWith(`:${today}`)) stale.push(k)
        continue
      }

      const writtenAt = Number.parseInt(
        window.localStorage.getItem(k) ?? '',
        10
      )
      if (!Number.isFinite(writtenAt) || writtenAt < cutoff) stale.push(k)
    }

    for (const k of stale) window.localStorage.removeItem(k)
  } catch {
    // See hasFired.
  }
}

/**
 * Shared with `metaAdsConversion.ts` so both Meta transports sweep the same
 * store on the same rules. Exported rather than duplicated: two copies would
 * drift, and a prefix missing from one of them grows without bound silently.
 */
export const pruneStaleMetaConversionMarks = pruneStaleMarks

/**
 * Only ever called once `fbq` was present and did not throw. The mark is
 * durable for the rest of the day, so writing it on a no-op (the pixel stub
 * not attached yet, or the pixel removed) would suppress the conversion until
 * tomorrow with no way to retry.
 */
const markFired = (key: string): void => {
  firedConversions.add(key)
  pruneStaleMarks()

  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${key}`, `${Date.now()}`)
  } catch {
    // See hasFired: privacy-restricted browsers throw here.
  }
}

/**
 * Guard, dispatch, and mark — in that order, in one place, mirroring
 * `googleAdsConversions.fireOnce` (whose JSDoc records the accepted limits of
 * this shape; they apply here unchanged).
 *
 * `eventID` is deliberately the transaction id rather than a random UUID.
 * Meta collapses repeats of the same event name + `eventID`, so a
 * deterministic id makes Meta itself dedupe what our session mark cannot see:
 * the same search re-fired from another tab, and the server-side CAPI copy of
 * this very search (`metaAdsConversion.ts`, which stamps this same id as an
 * `event_id` property). `googleAdsConversions` stamps it too, for its own
 * platforms.
 *
 * ⚠️ Meta only collapses repeats of the same event NAME + `eventID`. The CAPI
 * destination's Meta-facing `eventName` must therefore match the name below,
 * and its `eventId` input must be pointed at our property rather than left on
 * the `{event.uuid}` default — both are pre-launch gates in
 * docs/features/active/zaraz-migration/TODO.md.
 */
const fireOnce = (
  event: string,
  transactionId: string,
  extraParams?: Record<string, unknown>
): void => {
  const key = `${event}:${transactionId}`
  if (hasFired(key)) return

  if (typeof window === 'undefined' || !window.fbq) return

  try {
    // `transaction_id` last: it is the key this function just deduped on, so a
    // caller's extras must never be able to overwrite it and leave the stored
    // mark disagreeing with what went on the wire.
    window.fbq(
      'trackCustom',
      event,
      { ...extraParams, transaction_id: transactionId },
      { eventID: transactionId }
    )
  } catch {
    // Analytics must not break the app, and an unmarked failure stays
    // retryable on the next search.
    return
  }

  markFired(key)
}

/**
 * Meta pixel `Performed Dupe` for an executed search (DUP-2091), capped at one
 * per user per day (DUP-2188). See the module doc for why this is the existing
 * Meta event name, not a new one.
 *
 * The cap's identity, date rules and accepted limits live in
 * `dailyConversionId.ts`, which TikTok shares since DUP-2261. Two layers enforce
 * it here, and they cover DIFFERENT failures:
 *   1. our own guard in `fireOnce`, which stops the redundant `fbq` call, and
 *   2. Meta itself, collapsing repeats of one event name + `eventID` — which
 *      covers the SAME browser losing its mark (cleared storage, private
 *      mode, a quota or policy failure in the try/catch). It does NOT cover a
 *      second device; nothing here can.
 *
 * `conversionId` should come from `dailyConversionId`. Callers that cannot
 * identify the user fall back to a per-search id — an uncapped conversion is
 * the conservative failure, since the alternative is losing it entirely.
 */
export const trackMetaDupePerformed = ({
  conversionId,
  searchType,
}: {
  conversionId: string
  searchType: DupeSearchType
}): void => {
  fireOnce(AnalyticsEventName.PerformedDupe, conversionId, {
    search_type: searchType,
  })
}

const pendingRegistrations = new Set<string>()

/**
 * Called only for non-anonymous users after maybe-register confirms existing
 * === false. The Firebase
 * uid matches the other platforms' account id; registration is never daily.
 * Wait for the pixel queue stub: unlike a search, signup will not happen again.
 * After 10s we give up without marking sent (no persistence across navigation).
 *
 * Before enabling PostHog's Meta destination, remove its historical Signed Up
 * mapping (User Signed In -> CompleteRegistration): it counts returning logins
 * with unrelated event ids. See the Zaraz migration TODO pre-launch gates.
 * Stape forwarding/deduplication must be verified in Events Manager, not assumed.
 */
export const trackMetaAccountCreation = (userId: string): void => {
  if (typeof window === 'undefined') return
  const key = `CompleteRegistration:${userId}`
  if (hasFired(key) || pendingRegistrations.has(key)) return
  pendingRegistrations.add(key)
  const deadline = Date.now() + 10_000

  const dispatch = (): void => {
    // Recheck storage in case another tab dispatched while we were waiting.
    if (hasFired(key)) {
      pendingRegistrations.delete(key)
      return
    }
    if (window.fbq) {
      try {
        window.fbq(
          'track',
          'CompleteRegistration',
          { transaction_id: userId },
          { eventID: userId }
        )
        markFired(key)
        pendingRegistrations.delete(key)
        return
      } catch {
        // A failed dispatch must neither interrupt signup nor mark it sent.
      }
    }
    if (Date.now() >= deadline) {
      pendingRegistrations.delete(key)
      return
    }
    window.setTimeout(dispatch, 200)
  }

  dispatch()
}
